import {
  BackEndValueType,
  DashboardState,
  DeviceType,
  ECardinality,
  ESymbolCategory,
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  IComponent,
  IDisplayItem,
  IDisplayItemProperty,
  IKTypeField,
  IProperty,
  IScreenInput,
  ISymbol
} from "@kuika/kuika-cl-model";
import { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import {
  IApplyPropertyGridChangesToDisplayParameters,
  IChangeSelectedPropertyTypeParameters
} from "../../models/interface/function-parameters";
import { ProjectService } from "../../services/project-service";
import { ScreenService } from "../../services/screen-service";
import { AttachablePlatformContext, getAttachableActionDefinitions } from "./bindables/attachable-actions";
import {
  actionDefsToManifestBindings,
  BindablePropertySlot,
  BindablesManifest,
  BindablesManifestEntry,
  composePropertySlot,
  isSlotNarrowingActive,
  MANIFEST_BINDING_CATEGORIES,
  narrowPropertySlot,
  SlotNarrowing
} from "./bindables/manifest-composer";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import {
  DesignerSymbolPickerEngine,
  getDesignerSymbolPickerOptionLabel,
  getDesignerSymbolPickerSelectedOptionsByPath,
  IDesignerSymbolPickerOption,
  ISymbolPickerCascaderProps
} from "./designer-symbol-picker-engine";
import {
  safeParseJsonish,
  parseOptionPath,
  getScreenDictionary,
  getScreenDesign,
  setScreenDesign
} from "./designer-utils";
import { resolveEditTarget, type IScreenScopedInput } from "./detached-screen-session";
import { KMainFunctions } from "./main-functions";
import {
  buildManagedScreenBindingSymbol,
  findManagedScreenBindingSource,
  listManagedScreenBindingSources,
  managedBindingContextFromSdState
} from "./managed-screen-binding-sources";
import { findManagedTaskScreenLink } from "./managed-task-screen";
import { PropertyHelper } from "./property-helper";
import { buildTableFieldBindingSymbolFields, PropertySdHelper } from "./property-sd-helper";
import { ScreenDesignHelper } from "./screen-design-helper";

// These mirror the sets in designer-property-data-editor-actions.ts.
// Properties with these tool types go through the data editor flow in the UI
// (property-form-item.tsx), which clones the symbol and preserves fixedValue/category.
const DATA_EDITOR_TOOL_TYPES = new Set<string>([
  "Options",
  "TableOptions",
  "GoogleMapsOptions",
  "TreeOptions",
  "KanbanBoardLaneOptions",
  "KanbanBoardCardOptions",
  "NodeTreeNodeOptions",
  "ChartOptions",
  "CalendarOptions",
  "MenuOptions",
  "BreadcrumbOptions",
  "RadialChartOptions",
  "RadialChartLevelOptions",
  "MixedChartOptions",
  "MixedChartLineOptions",
  "MixedChartPointOptions",
  "MixedChartBarOptions",
  "TimelineOptions",
  "ChatOptions"
]);

/**
 * Returns the property name if the given property is a data-editor property
 * (isOptionsProp, isTableOptionsProp, etc.) that requires complex symbol
 * configuration via the data editor UI. Returns null if the property can
 * safely be set as a simple fixed value.
 */
export function isDataEditorProperty(
  sd: IScreenDesignContextState,
  di: IDisplayItem,
  propertyName: string
): string | null {
  const property = getResolvedPropertyModel(sd, di, propertyName);
  if (!property) return null;

  const p = property as any;
  if (
    p.isOptionsProp === true ||
    p.isTableOptionsProp === true ||
    p.isGoogleMapsOptionsProp === true ||
    p.isCalendarOptionsProp === true ||
    p.isMenuOptionsProp === true ||
    p.isBreadcrumbOptionsProp === true ||
    p.isKanbanBoardLaneOptionsProp === true ||
    p.isKanbanBoardCardOptionsProp === true ||
    p.isNodeTreeNodeOptionsProp === true ||
    p.isChatOptionsProp === true ||
    p.isTreeOptionsProp === true ||
    p.isDirectoryOptionsProp === true ||
    p.isSpecificDatesProp === true ||
    p.isLanguageOptionsProp === true ||
    p.isRadialChartOptionsProp === true ||
    p.isRadialChartLevelOptionsProp === true ||
    p.isMixedChartOptionsProp === true ||
    p.isMixedChartLineOptionsProp === true ||
    p.isMixedChartPointOptionsProp === true ||
    p.isMixedChartBarOptionsProp === true ||
    p.isChartOptionsProp === true ||
    p.isTimelineOptionsProp === true
  ) {
    return propertyName;
  }

  const componentDef = getComponentDefinition(sd, di);
  if (componentDef?.properties) {
    const defProp = componentDef.properties.find((dp) => dp.name === propertyName);
    if (defProp) {
      const propertyHelper = new PropertyHelper();
      const toolType = propertyHelper.calculatePropertyToolType(di, defProp);
      if (DATA_EDITOR_TOOL_TYPES.has(toolType)) {
        return propertyName;
      }
    }
  }

  return null;
}

/**
 * The platform's default-value registry — the single source of truth for what a
 * `default` symbol may be named. `name` is the persisted form (what codegen
 * resolves); `label` is what the picker shows.
 *
 * Exported because it was being hand-copied elsewhere and the copy drifted: the
 * validator's list spelled the user id `CurrentUserId` while the registry — and
 * therefore codegen — says `CurrentUserid`, so a correctly-pinned parameter got
 * warned about and eleven real names were missing entirely. Read the registry;
 * never restate it (KOA-18136).
 */
export interface DefaultValueEntry {
  /** Persisted symbol name — what codegen resolves. */
  name: string;
  /** Value type the default produces. */
  type: string;
  /** Human-facing name shown in the property picker. */
  label: string;
}

export const DEFAULT_VALUES: DefaultValueEntry[] = [
  { name: "DateNow", type: "DateTimeOffset", label: "Now" },
  { name: "Empty", type: "String", label: "Empty" },
  { name: "Space", type: "String", label: "Space" },
  { name: "True", type: "Boolean", label: "True" },
  { name: "False", type: "Boolean", label: "False" },
  { name: "NewGuid", type: "Guid", label: "New Guid" },
  { name: "EmptyGuid", type: "Guid", label: "Empty Guid" },
  { name: "Null", type: "any", label: "null" },
  { name: "GetMyUsername", type: "String", label: "Current Username" },
  { name: "CurrentUserid", type: "Guid", label: "Current Userid" },
  { name: "CurrentUserFirstName", type: "String", label: "Current First Name" },
  { name: "CurrentUserLastName", type: "String", label: "Current Last Name" },
  { name: "CurrentUserFullName", type: "String", label: "Current Full Name" },
  { name: "CurrentUserPhoneNumber", type: "String", label: "Current Phone Number" },
  { name: "CurrentUserRoleNames", type: "String", label: "Current Role Names" },
  { name: "CurrentURL", type: "String", label: "Current URL" },
  { name: "ClientIpAddress", type: "String", label: "Client IP Address" },
  { name: "GetVersionInfo", type: "String", label: "Version Info" }
];

/**
 * True for the current-user identity `DEFAULT_VALUES` names whose persisted
 * `defaultValueName` must be the LABEL form (see {@link buildDesignerSymbol}
 * `default` branch): `GetMyUsername` + the `CurrentUser*` family. `CurrentURL`
 * is deliberately excluded — it is not a user-identity attribute.
 */
function isCurrentUserDefaultName(name: string): boolean {
  const lower: string = name.toLowerCase();
  return lower === "getmyusername" || lower.startsWith("currentuser");
}

/**
 * Defaults the symbol picker offers that carry no registry key — the picker
 * persists their LABEL as `defaultValueName`, so the label is the only valid
 * spelling. Kept beside {@link DEFAULT_VALUES} so "is this a real default?" has
 * one answer.
 */
const PICKER_ONLY_DEFAULT_VALUES: DefaultValueEntry[] = [
  { name: "Is MicroApp", type: "Boolean", label: "Is MicroApp" },
  { name: "Custom Binding", type: "Unknown", label: "Custom Binding" },
  { name: "Current Page Number", type: "String", label: "Current Page Number" },
  { name: "Total Page Number", type: "String", label: "Total Page Number" }
];

export interface IResolvedDefaultValue {
  /** What is persisted on the symbol — see the KOA-17362 note in `buildDesignerSymbol`. */
  defaultValueName: string;
  label: string;
  type: string;
}

const describeValidDefaultNames = (): string =>
  DEFAULT_VALUES.map((item: DefaultValueEntry): string =>
    item.name === item.label ? item.name : `${item.name} ("${item.label}")`
  ).join(", ");

/**
 * Resolve a caller-supplied default name to the registry entry. Accepts the
 * registry key (`GetMyUsername`) and the picker label (`Current Username`)
 * interchangeably, case-insensitively, with an optional `default:` prefix.
 * Anything else is an ERROR naming the valid defaults: an unknown name stored
 * verbatim is a binding codegen cannot resolve, reported as success.
 */
export function resolveDefaultValueName(rawName: string): IResolvedDefaultValue | IErrorResult {
  const trimmed: string = rawName.trim();
  if (/^default\./i.test(trimmed)) {
    return {
      error: `"${trimmed}" uses the dot form — defaults are written with a colon: "default:${trimmed.slice("default.".length)}".`
    };
  }
  const name: string = /^default:/i.test(trimmed) ? trimmed.slice("default:".length).trim() : trimmed;
  const lower: string = name.toLowerCase();
  const found: DefaultValueEntry | undefined = [...DEFAULT_VALUES, ...PICKER_ONLY_DEFAULT_VALUES].find(
    (item: DefaultValueEntry): boolean => item.name.toLowerCase() === lower || item.label.toLowerCase() === lower
  );
  if (!found) {
    return {
      error: `Unknown default "${name}". Valid defaults (name or "label"): ${describeValidDefaultNames()}. For a hard-coded value use a literal / fixed value instead of a default.`
    };
  }
  // KOA-17362 — current-user identity defaults persist the human-facing LABEL
  // ("Current Username"), not the registry name ("GetMyUsername"), so a
  // programmatic binding is byte-identical to a hand-made symbol-picker one (the
  // picker stores `node.label`). Codegen resolves BOTH forms, so this is a
  // consistency fix, not a runtime one. SCOPED to the current-user family on
  // purpose: NewGuid / DateNow / EmptyGuid keep their registry name (a prior
  // blanket label-persist churned NewGuid needlessly and was reverted).
  const defaultValueName: string = isCurrentUserDefaultName(found.name) ? found.label : found.name;
  return { defaultValueName, label: found.label, type: found.type };
}

// ---------------------------------------------------------------------------
// Interfaces for input parameters on exported functions
// ---------------------------------------------------------------------------

export interface ISetPropertyValueInput extends IScreenScopedInput {
  kuikaId: string;
  propertyName: string;
  value: unknown;
}

export interface ISetPropertySymbolInput extends IScreenScopedInput {
  kuikaId: string;
  propertyName: string;
  symbol: ISymbol;
  allowDynamicProperty?: boolean;
}

export interface IUpdatePropertySymbolInput extends IScreenScopedInput {
  kuikaId: string;
  propertyName: string;
  symbolPatch?: unknown;
  patch?: unknown;
}

export interface ISetPropertyTypeInput extends IScreenScopedInput {
  kuikaId: string;
  propertyName: string;
  selectedType: number | string;
}

export interface ISetPropertyTypeOptionsInput extends IScreenScopedInput {
  kuikaId: string;
  propertyName: string;
  selectedTypeOptions: unknown;
}

export interface IPropertyEntry {
  propertyName: string;
  value: unknown;
}

export interface IListBindingOptionsInput extends IScreenScopedInput {
  kuikaId?: string;
  propertyName?: string;
  targetTypeName?: string;
  targetCardinality?: number | string;
  categoryName?: string;
  symbol?: unknown;
  hideFixedValue?: boolean;
  hideWFParams?: boolean;
  action?: unknown;
  parameter?: unknown;
  hideActionResults?: boolean;
  showInSingleLine?: boolean;
  isGenericSelect?: boolean;
  isObject?: boolean;
  isConditionPicker?: boolean;
  hideMiInstanceCountParam?: boolean;
  genericSelectTables?: unknown;
  genericSelectTableModels?: unknown;
}

export interface ISetPropertyBindingInput extends IListBindingOptionsInput {
  bindingType?: string;
  optionPath?: unknown;
  pickerPath?: unknown;
  selectedPath?: unknown;
  defaultName?: string;
  inputName?: string;
  sourceKuikaId?: string;
  sourcePropertyName?: string;
  actionId?: string;
  sourceActionId?: string;
  fieldName?: string;
}

export interface IBuildDesignerSymbolInput extends IScreenScopedInput {
  bindingType: string;
  defaultName?: string;
  inputName?: string;
  /**
   * The BOUND-TO side's declared type (an action parameter's `Typename`), when
   * the caller knows it. KOA-18136 — a process-bag value is always a String at
   * runtime; recording the target type is how a hand-picked binding notes the
   * conversion, so a programmatic one must do the same. Ignored by the binding
   * types that carry their own typing.
   */
  targetType?: string;
  sourceKuikaId?: string;
  sourcePropertyName?: string;
  actionId?: string;
  sourceActionId?: string;
  fieldName?: string;
  /** Literal value for `bindingType: "fixed"` / `"constant"` (e.g. a confirmation message). */
  fixedValue?: string;
}

export interface IMicroAppProjectInput {
  projectId?: string;
}

export interface IMicroAppScreenInputsInput {
  screenId?: string;
  kuikaId?: string;
}

export interface ISetMicroAppProjectInput {
  kuikaId: string;
  projectId: string;
  projectPropertyName?: string;
  startingScreenPropertyName?: string;
}

export interface ISetMicroAppStartingScreenInput {
  kuikaId: string;
  screenId?: string;
  startingScreenId?: string;
  propertyName?: string;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

interface ISuccessResult {
  success: true;
  message: string;
}

interface IErrorResult {
  success?: false;
  error: string;
}

interface ISuccessResultWithSkipped extends ISuccessResult {
  skipped?: string[];
}

interface ICollectedAction {
  actionId: string;
  label: string;
  actionDefinitionId: string | undefined;
  eventName: string;
  componentKuikaId: string;
  returnType: string | undefined;
  returnCardinality: ECardinality | undefined;
}

interface ISymbolPickerContext {
  di: IDisplayItem | undefined;
  property: IDisplayItemProperty | IProperty | undefined;
  targetType: string;
  targetCardinality: number;
  engine: DesignerSymbolPickerEngine;
}

interface ILegacyBindingCategories {
  defaults: { name: string; label: string; type: string }[];
  screenInputs: { name: string; type: string; cardinality: number }[];
  componentValues: {
    diId: number | undefined;
    componentName: string;
    friendlyName: undefined;
    propertyName: string;
    propertyType: string;
  }[];
  actionResults: {
    actionId: string;
    label: string;
    actionDefinitionId: string | undefined;
    returnType: string;
    returnCardinality: number;
  }[];
  currentRow?: { fieldName: string; fieldType: string; path: number[] }[];
}

// ---------------------------------------------------------------------------
// Iframe kuika window augmentation
// ---------------------------------------------------------------------------

interface IKuikaIframeWindow extends Window {
  kuika?: {
    screensData?: Record<string, Record<string, { id: string; name: string }>>;
  };
}

// ---------------------------------------------------------------------------
// Internal accessor functions
// ---------------------------------------------------------------------------

function getScreenInputs(sd: IScreenDesignContextState): IScreenInput[] {
  return sd?.getScreenInputs?.() || [];
}

function getActionDefinitions(sd: IScreenDesignContextState): IActionDefinitionDto[] {
  return sd?.actionDefinitions || [];
}

function getComponentDefinitions(sd: IScreenDesignContextState): IComponent[] {
  return sd?.project?.componentSetObj?.components || [];
}

function applyPropertyGridChange(
  sd: IScreenDesignContextState,
  params: IApplyPropertyGridChangesToDisplayParameters,
  changeReason: ESdChangeReason
): void {
  sd?.applyPropertyGridChangesToDisplay?.(params, changeReason);
}

export function applyPropertyGridChangeWithoutCommit(
  sd: IScreenDesignContextState,
  params: IApplyPropertyGridChangesToDisplayParameters,
  changeReason: ESdChangeReason
): boolean {
  return new PropertySdHelper(() => sd).applyPropertyGridChangesToDisplay(params, changeReason, false);
}

function changeSelectedPropertyType(
  sd: IScreenDesignContextState,
  params: IChangeSelectedPropertyTypeParameters
): void {
  sd?.changeSelectedPropertyType?.(params);
}

function syncColResponsive(
  sdInstance: ScreenDesignInstance,
  sd: IScreenDesignContextState,
  kuikaId: string,
  value: string
): void {
  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (di?.item?.name === "Col" && di.parentId) {
    const rowDI = dict[di.parentId];
    if (rowDI?.item?.name === "Row" && rowDI.children) {
      // dragDrop lives on the ScreenDesignContextProvider instance, not on IScreenDesignContextState.
      const sdWithDragDrop = sdInstance as unknown as {
        dragDrop?: {
          resizeColumnValuesByIndex: (
            rowDI: IDisplayItem,
            device: DeviceType,
            colIndex: number,
            value: number,
            isTabletDisable: boolean,
            isPhoneDisable: boolean
          ) => void;
        };
      };
      if (sdWithDragDrop.dragDrop?.resizeColumnValuesByIndex) {
        const colIndex = rowDI.children.indexOf(di);
        if (colIndex >= 0) {
          sdWithDragDrop.dragDrop.resizeColumnValuesByIndex(
            rowDI,
            DeviceType.Desktop,
            colIndex,
            parseInt(value, 10),
            true,
            true
          );
          const screenDesign = getScreenDesign(sd);
          setScreenDesign(sd, screenDesign, ESdChangeReason.ChangeResponsiveRowColumnSettings, rowDI.id.toString());
        }
      }
    }
  }
}

function validateKuikaId(sd: IScreenDesignContextState, kuikaId: string): { di: IDisplayItem } | { error: string } {
  const dict = getScreenDictionary(sd);
  const di = dict[parseInt(kuikaId, 10)];
  if (!di || !di.item) {
    return { error: `Component with kuikaId "${kuikaId}" not found` };
  }
  return { di };
}

function getValidPropertyNames(sd: IScreenDesignContextState, di: IDisplayItem): Set<string> {
  const names = new Set<string>();

  const componentDef = getComponentDefinitions(sd).find((component) => component.id === di.item.id);
  if (componentDef?.properties) {
    for (const property of componentDef.properties) {
      if (property.selectedType === 3) continue;
      if (property.isTechnical) continue;
      if (property.isDeprecated) continue;
      names.add(property.name);
    }
  }

  if (di.item.properties) {
    for (const property of di.item.properties) {
      if (property.selectedType === 3) continue;
      names.add(property.name);
    }
  }

  return names;
}

function validatePropertyName(
  sd: IScreenDesignContextState,
  di: IDisplayItem,
  propertyName: string
): { error: string } | null {
  const validNames = getValidPropertyNames(sd, di);
  if (validNames.has(propertyName)) return null;

  const componentName = di.item?.name || "Unknown";
  const available = Array.from(validNames).sort();
  const displayList =
    available.length > 20
      ? available.slice(0, 20).join(", ") + `, ... (${available.length} total)`
      : available.join(", ");

  return {
    error: `Property "${propertyName}" not found on ${componentName} (kuikaId: ${di.id}). Available: [${displayList}]`
  };
}

function isDynamicMicroAppScreenInputProperty(di: IDisplayItem, propertyName: string): boolean {
  return di?.item?.name === "MicroApp" && typeof propertyName === "string" && propertyName.startsWith("screenInput_");
}

function shouldAllowDynamicPropertyCreation(
  di: IDisplayItem,
  propertyName: string,
  allowDynamicProperty?: boolean
): boolean {
  return allowDynamicProperty === true || isDynamicMicroAppScreenInputProperty(di, propertyName);
}

function getFixedSymbol(value: unknown, description = "", label = ""): ISymbol {
  return {
    category: ESymbolCategory.fixed,
    description,
    label,
    fixedValue: value
  } as ISymbol;
}

function setScreensDataInActiveIframe(projectId: string, screens: { id: string; name: string }[]): void {
  try {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
    const contentWindow = iframe?.contentWindow as IKuikaIframeWindow | null;
    if (!contentWindow) return;

    if (!contentWindow.kuika) contentWindow.kuika = {};
    if (!contentWindow.kuika.screensData) contentWindow.kuika.screensData = {};

    contentWindow.kuika.screensData[projectId] = screens.reduce(
      (acc: Record<string, { id: string; name: string }>, screen) => {
        acc[screen.id] = { id: screen.id, name: screen.name };
        return acc;
      },
      {} as Record<string, { id: string; name: string }>
    );
  } catch {
    // Cross-origin iframe access may throw -- safe to ignore
  }
}

function getMicroAppScreenInputProperties(di: IDisplayItem): IDisplayItemProperty[] {
  return (di?.item?.properties || []).filter((property: IDisplayItemProperty) =>
    property?.name?.startsWith?.("screenInput_")
  );
}

function collectActionsFromAllDIs(displayItems: IDisplayItem[]): ICollectedAction[] {
  const results: ICollectedAction[] = [];

  function walkActions(actions: IAction[], di: IDisplayItem, eventName: string) {
    for (const action of actions || []) {
      results.push({
        actionId: action.id,
        label: action.label || action.description || "Unnamed",
        actionDefinitionId: action.actionDefinitionId || undefined,
        eventName,
        componentKuikaId: di.id?.toString(),
        returnType: action.returnType || undefined,
        returnCardinality: action.returnCardinality
      });
      if (action.ifThenElseData) {
        walkActions(action.ifThenElseData.ifActions || [], di, eventName);
        walkActions(action.ifThenElseData.elseActions || [], di, eventName);
      }
    }
  }

  function walkDi(di: IDisplayItem) {
    for (const event of di.item?.events || []) {
      walkActions(event.actions, di, event.eventName);
    }
    for (const child of di.children || []) {
      walkDi(child);
    }
  }

  for (const di of displayItems) {
    walkDi(di);
  }

  return results;
}

function parseNumericEnum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getComponentDefinition(sd: IScreenDesignContextState, di: IDisplayItem): IComponent | undefined {
  return getComponentDefinitions(sd).find((component) => component.id === di?.item?.id);
}

function getResolvedPropertyModel(
  sd: IScreenDesignContextState,
  di: IDisplayItem,
  propertyName: string
): IDisplayItemProperty | IProperty | undefined {
  const instanceProperty = di?.item?.properties?.find(
    (property: IDisplayItemProperty) => property.name === propertyName
  );
  if (instanceProperty) return instanceProperty;

  return (
    getComponentDefinition(sd, di)?.properties?.find((property: IProperty) => property.name === propertyName) ||
    undefined
  );
}

function getResolvedPropertyTypeName(property: IDisplayItemProperty | IProperty | undefined): string | null {
  if (!property) return null;
  // IProperty (which extends IPropertyDefinition & IDisplayItemProperty) may have domainTypeName.
  // The symbol on the property may carry targetType. We check multiple runtime shapes.
  const prop = property as IDisplayItemProperty & Partial<IProperty>;
  return (
    prop.symbol?.targetType || ((prop as unknown as Record<string, unknown>).typeName as string | undefined) || null
  );
}

function getResolvedPropertyCardinality(property: IDisplayItemProperty | IProperty | undefined): number | null {
  if (!property) return null;
  const prop = property as IDisplayItemProperty & { cardinality?: number; targetCardinality?: number };
  if (prop.cardinality != null) return prop.cardinality;
  if (prop.targetCardinality != null) return prop.targetCardinality;
  if (prop.symbol?.targetCardinality != null) return prop.symbol.targetCardinality;
  return null;
}

function createDesignerSymbolPickerContext(
  sd: IScreenDesignContextState,
  input: IListBindingOptionsInput
): ISymbolPickerContext | IErrorResult {
  let di: IDisplayItem | undefined;
  let property: IDisplayItemProperty | IProperty | undefined;

  if (input.kuikaId) {
    const lookup = validateKuikaId(sd, input.kuikaId);
    if ("error" in lookup) return lookup;
    di = lookup.di;
  }

  if (input.propertyName && di) {
    const propError = validatePropertyName(sd, di, input.propertyName);
    if (propError) return propError;
    property = getResolvedPropertyModel(sd, di, input.propertyName);
  }

  const targetType = input.targetTypeName || getResolvedPropertyTypeName(property) || "String";
  const targetCardinality = parseNumericEnum(input.targetCardinality) ?? getResolvedPropertyCardinality(property) ?? 1;

  const engineProps: ISymbolPickerCascaderProps & { sd: IScreenDesignContextState } = {
    sd,
    categoryName: input.categoryName || input.propertyName || "Binding",
    targetTypeName: targetType,
    targetCardinality: targetCardinality as ECardinality,
    targetDI: di,
    symbol: safeParseJsonish(input.symbol) as ISymbol | undefined,
    hideFixedValue: input.hideFixedValue === true,
    hideWFParams: input.hideWFParams === true,
    action: safeParseJsonish(input.action) as IAction | undefined,
    parameter: safeParseJsonish(input.parameter) as ISymbolPickerCascaderProps["parameter"],
    hideActionResults: input.hideActionResults === true,
    showInSingleLine: input.showInSingleLine === true,
    isGenericSelect: input.isGenericSelect === true,
    isObject: input.isObject === true,
    isConditionPicker: input.isConditionPicker === true,
    hideMiInstanceCountParam: input.hideMiInstanceCountParam === true,
    property: property as IProperty | undefined,
    genericSelectTables: safeParseJsonish(
      input.genericSelectTables
    ) as ISymbolPickerCascaderProps["genericSelectTables"],
    genericSelectTableModels: safeParseJsonish(
      input.genericSelectTableModels
    ) as ISymbolPickerCascaderProps["genericSelectTableModels"]
  };

  const engine = new DesignerSymbolPickerEngine(
    engineProps as ISymbolPickerCascaderProps & { sd: IScreenDesignContextState }
  );

  return {
    di,
    property,
    targetType,
    targetCardinality,
    engine
  };
}

function walkDesignerSymbolPickerOptions(
  options: CascaderOptionType[],
  visitor: (node: CascaderOptionType, ancestors: CascaderOptionType[], path: number[]) => void,
  ancestors: CascaderOptionType[] = [],
  path: number[] = []
): void {
  options.forEach((node, index) => {
    const currentPath = [...path, index];
    visitor(node, ancestors, currentPath);

    if (Array.isArray(node.children) && node.children.length > 0) {
      walkDesignerSymbolPickerOptions(node.children, visitor, [...ancestors, node], currentPath);
    }
  });
}

function buildLegacyBindingCategoriesFromOptions(options: CascaderOptionType[]): ILegacyBindingCategories {
  const defaults = new Map<string, { name: string; label: string; type: string }>();
  const screenInputs = new Map<string, { name: string; type: string; cardinality: number }>();
  const componentValues = new Map<
    string,
    {
      diId: number | undefined;
      componentName: string;
      friendlyName: undefined;
      propertyName: string;
      propertyType: string;
    }
  >();
  const actionResults = new Map<
    string,
    {
      actionId: string;
      label: string;
      actionDefinitionId: string | undefined;
      returnType: string;
      returnCardinality: number;
    }
  >();
  const currentRow: { fieldName: string; fieldType: string; path: number[] }[] = [];

  walkDesignerSymbolPickerOptions(options, (node, ancestors, path) => {
    if (node.category === ESymbolCategory.default && (!node.children || node.children.length === 0)) {
      const label = getDesignerSymbolPickerOptionLabel(node);
      const defaultValue = DEFAULT_VALUES.find(
        (item) => item.label.toLowerCase() === label.toLowerCase() || item.name.toLowerCase() === label.toLowerCase()
      );
      defaults.set(label.toLowerCase(), {
        name: defaultValue?.name || label,
        label,
        type: (node.toType as string) || defaultValue?.type || (node.currentType as string) || "String"
      });
    }

    if (node.category === ESymbolCategory.screenInput && node.inputName) {
      screenInputs.set(node.inputName as string, {
        name: node.inputName as string,
        type:
          (node.domainType as { name?: string })?.name ||
          (node.domainType as string) ||
          (node.currentType as string) ||
          "String",
        cardinality: (node.cardinality as number) ?? 1
      });
    }

    if (node.category === ESymbolCategory.displayItemProp) {
      const displayItemNode = [...ancestors]
        .reverse()
        .find((ancestor) => ancestor.category === ESymbolCategory.displayItem);
      const propertyName = typeof node.label === "string" ? node.label : getDesignerSymbolPickerOptionLabel(node);
      const key = `${displayItemNode?.diId}:${propertyName}`;
      componentValues.set(key, {
        diId: displayItemNode?.diId as number | undefined,
        componentName: getDesignerSymbolPickerOptionLabel(displayItemNode!) || "Component",
        friendlyName: undefined,
        propertyName,
        propertyType:
          (node.domainType as { name?: string })?.name ||
          (node.domainType as string) ||
          (node.currentType as string) ||
          "String"
      });
    }

    if (node.category === ESymbolCategory.actionResult && node.resultActionId) {
      const actionDefinition = node.actionDefinition as { id?: string; returnCardinality?: number } | undefined;
      actionResults.set(node.resultActionId as string, {
        actionId: node.resultActionId as string,
        label: getDesignerSymbolPickerOptionLabel(node),
        actionDefinitionId: actionDefinition?.id,
        returnType:
          (node.domainType as { name?: string })?.name ||
          (node.domainType as string) ||
          (node.currentType as string) ||
          "Unknown",
        returnCardinality: actionDefinition?.returnCardinality ?? (node.cardinality as number) ?? 1
      });
    }

    if (
      node.category === ESymbolCategory.fieldOfType &&
      ancestors.length > 0 &&
      ancestors[ancestors.length - 1].category === ESymbolCategory.current &&
      (!node.children ||
        node.children.length === 0 ||
        node.children.every((c: CascaderOptionType) => c.category !== ESymbolCategory.fieldOfType))
    ) {
      const fieldName = getDesignerSymbolPickerOptionLabel(node);
      currentRow.push({
        fieldName,
        fieldType:
          (node.domainType as { name?: string })?.name ||
          (node.domainType as string) ||
          (node.currentType as string) ||
          "String",
        path
      });
    }
  });

  const result: ILegacyBindingCategories = {
    defaults: Array.from(defaults.values()),
    screenInputs: Array.from(screenInputs.values()),
    componentValues: Array.from(componentValues.values()),
    actionResults: Array.from(actionResults.values())
  };

  if (currentRow.length > 0) {
    result.currentRow = currentRow;
  }

  return result;
}

function findDesignerSymbolPickerPath(
  options: CascaderOptionType[],
  predicate: (node: CascaderOptionType, ancestors: CascaderOptionType[], path: number[]) => boolean,
  ancestors: CascaderOptionType[] = [],
  path: number[] = []
): number[] | null {
  for (let index = 0; index < options.length; index++) {
    const node = options[index];
    const currentPath = [...path, index];
    if (predicate(node, ancestors, currentPath)) {
      return currentPath;
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
      const found = findDesignerSymbolPickerPath(node.children, predicate, [...ancestors, node], currentPath);
      if (found) return found;
    }
  }

  return null;
}

function resolveLegacyBindingPath(options: CascaderOptionType[], input: ISetPropertyBindingInput): number[] | null {
  switch (input.bindingType) {
    case "default": {
      const requested = String(input.defaultName || "").toLowerCase();
      if (!requested) return null;
      const defaultValue = DEFAULT_VALUES.find(
        (item) => item.name.toLowerCase() === requested || item.label.toLowerCase() === requested
      );
      const candidates = new Set(
        [requested, defaultValue?.label?.toLowerCase(), defaultValue?.name?.toLowerCase()].filter(Boolean) as string[]
      );

      return findDesignerSymbolPickerPath(
        options,
        (node) =>
          node.category === ESymbolCategory.default &&
          (!node.children || node.children.length === 0) &&
          candidates.has(getDesignerSymbolPickerOptionLabel(node).toLowerCase())
      );
    }

    case "screenInput": {
      if (!input.inputName) return null;
      return findDesignerSymbolPickerPath(
        options,
        (node) => node.category === ESymbolCategory.screenInput && node.inputName === input.inputName
      );
    }

    case "componentValue": {
      if (!input.sourceKuikaId || !input.sourcePropertyName) return null;
      return findDesignerSymbolPickerPath(options, (node, ancestors) => {
        if (node.category !== ESymbolCategory.displayItemProp) return false;
        const displayItemNode = [...ancestors]
          .reverse()
          .find((ancestor) => ancestor.category === ESymbolCategory.displayItem);
        const propertyName = typeof node.label === "string" ? node.label : getDesignerSymbolPickerOptionLabel(node);
        return (
          String(displayItemNode?.diId) === String(input.sourceKuikaId) && propertyName === input.sourcePropertyName
        );
      });
    }

    case "actionResult": {
      if (!input.actionId) return null;
      return findDesignerSymbolPickerPath(
        options,
        (node) => node.category === ESymbolCategory.actionResult && node.resultActionId === input.actionId
      );
    }

    case "actionField": {
      const resolvedActionId = input.sourceActionId || input.actionId;
      if (!resolvedActionId || !input.fieldName) return null;
      return findDesignerSymbolPickerPath(options, (node, ancestors) => {
        if (![ESymbolCategory.actionField, ESymbolCategory.fieldOfType].includes(node.category as ESymbolCategory))
          return false;
        const actionNode = [...ancestors].find((ancestor) => ancestor.category === ESymbolCategory.actionResult);
        if (actionNode?.resultActionId !== resolvedActionId) return false;

        const fieldSegments = [...ancestors, node]
          .filter((item) =>
            [ESymbolCategory.actionField, ESymbolCategory.fieldOfType].includes(item.category as ESymbolCategory)
          )
          .map(
            (item) =>
              (item.field as { name?: string })?.name || item.fieldName || getDesignerSymbolPickerOptionLabel(item)
          )
          .filter(Boolean);

        const fullFieldPath = fieldSegments.join(".");
        const currentFieldName = fieldSegments[fieldSegments.length - 1];
        return fullFieldPath === input.fieldName || currentFieldName === input.fieldName;
      });
    }

    default:
      return null;
  }
}

export function applyDesignerPropertyChange(
  sdInstance: ScreenDesignInstance,
  params: IApplyPropertyGridChangesToDisplayParameters,
  changeReason: ESdChangeReason = ESdChangeReason.ChangePropertyValue,
  screenId?: string | null
): void {
  // The screen is a PARAMETER rather than part of `params`: that type is the platform's shared
  // property-grid model, and which screen an edit lands on is this module's concern, not the
  // model's. Omitted → the open screen.
  const sd = resolveEditTarget(sdInstance, screenId);
  if (!sd || !params?.kuikaId || !params?.propertyName) return;

  if (params.value !== undefined && params.propertyName === "span") {
    syncColResponsive(sdInstance, sd, params.kuikaId, String(params.value));
  }

  applyPropertyGridChange(sd, params, changeReason);
}

function getFormatterForType(type: string): string {
  const lowerType = type.toLowerCase();
  if (lowerType === "date") return "date";
  if (lowerType === "datetime" || lowerType === "datetimeoffset") return "datetime";
  return "";
}

function applyDesignerPropertySymbolSideEffects(
  sd: IScreenDesignContextState,
  kuikaId: string,
  propertyName: string,
  symbol: ISymbol,
  shouldCommit: boolean = true
): boolean {
  if (!symbol?.currentType) return false;
  if (propertyName === "formatter") return false;
  if (sd?.dashboardState !== DashboardState.design) return false;

  const targetDi = getScreenDictionary(sd)?.[parseInt(kuikaId, 10)];
  if (targetDi?.item?.name !== "Label") return false;

  const properties: IDisplayItemProperty[] = targetDi?.item?.properties || [];
  const formatterProperty = properties.find((property) => property.name === "formatter");
  if (!formatterProperty?.symbol) return false;

  let type: string = symbol.currentType;
  if (symbol.category === ESymbolCategory.displayItemProp && symbol.diId) {
    const sourceDi = getScreenDictionary(sd)?.[parseInt(symbol.diId.toString(), 10)];
    if (sourceDi?.item?.name === "date") {
      type = "date";
    }
  }

  const formatterSymbol: ISymbol = { ...formatterProperty.symbol };
  formatterSymbol.category = ESymbolCategory.fixed;
  formatterSymbol.fixedValue = getFormatterForType(type);
  const params = {
    propertyName: "formatter",
    symbol: formatterSymbol,
    kuikaId,
    value: undefined
  };

  if (shouldCommit) {
    applyPropertyGridChange(sd, params, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    return true;
  }

  return applyPropertyGridChangeWithoutCommit(sd, params, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
}

/**
 * KOA-17717 — Resolve the REAL column type for a `current.<field>` row-context
 * binding by walking up from the trigger component to its enclosing repeater
 * (Table / GalleryView / list) and reading the field's type off that repeater's
 * bound dataSource (or options) action return-type model.
 *
 * Background: the deterministic AI path stamps `current.X` symbols with a
 * hardcoded `currentType/targetType: "String"` (buildDesignerSymbol's `current.X`
 * promotion + the Wirer/Binder's Fix #42 inline promotion in action-tools.ts).
 * A Guid/Int row key therefore lands as String, diverging from the manual
 * designer cascader which resolves the real type via the SAME enclosing-repeater
 * datasource lookup. Mirrors the existing `actionField` resolution in this file
 * (`element.type.name`, "String" fallback). Returns undefined when the host has
 * no resolvable enclosing repeater / matching field, so the caller keeps the
 * promotion's type — no regression, no broken binding.
 */
/**
 * Does `hostKuikaId` sit inside a data repeater — i.e. is there any row context
 * for a `current.<field>` symbol to resolve against?
 *
 * This is the same structural question the symbol picker answers before it will
 * even OFFER "Current Row": `generateCurrentRow` starts from
 * `getParents(di).filter(isDataBindingOptions)` and emits nothing when that set
 * is empty, so a human cannot pick `current.*` outside a repeater. Codegen agrees
 * — `ClientCodeGenerationBase`'s `Current` case returns null when
 * `FindParentHavingDataBindingOptions()` finds nothing, so the parameter ships
 * EMPTY. Only the programmatic path accepted it, which is how a screen-level
 * button on a form (no table anywhere) got `screenInputs: [{ExpenseId,
 * current.Id}]` and shipped dead.
 *
 * Structural on purpose: it asks whether an ancestor is a data-bound repeater,
 * NOT whether the field resolves. A repeater whose datasource is not configured
 * yet still provides row context (same tolerance as
 * `applyDataRepeaterOptionFiltering`'s `isStructurallyInsideDataRepeater`).
 * Returns true when the tree is unavailable — unknown is not proof of absence,
 * and a false rejection is worse than the status quo.
 */
export function hostHasRowContext(
  sdInstance: ScreenDesignInstance,
  hostKuikaId: string | number | undefined | null,
  screenId?: string | null
): boolean {
  if (hostKuikaId === undefined || hostKuikaId === null) return true;
  const sd: IScreenDesignContextState = resolveEditTarget(sdInstance, screenId);
  const dict: { [key: number]: IDisplayItem } = getScreenDictionary(sd);
  if (!dict) return true;
  let di: IDisplayItem | undefined = dict[Number(hostKuikaId)];
  if (!di) return true;
  let guard: number = 0;
  while (di && guard < 100) {
    guard += 1;
    const parentId: number | undefined | null = di.parentId;
    di = parentId !== undefined && parentId !== null ? dict[Number(parentId)] : undefined;
    if (!di) break;
    const isRepeater: boolean = (di.item?.properties ?? []).some(
      (p: IDisplayItemProperty) => p.name === "dataSource" || p.name === "options"
    );
    if (isRepeater) return true;
  }
  return false;
}

function resolveCurrentRowFieldType(
  sdInstance: ScreenDesignInstance,
  hostKuikaId: string | number | undefined | null,
  fieldName: string,
  screenId?: string | null
): string | undefined {
  if (hostKuikaId === undefined || hostKuikaId === null) return undefined;
  const sd: IScreenDesignContextState = resolveEditTarget(sdInstance, screenId);
  const dict: { [key: number]: IDisplayItem } = getScreenDictionary(sd);
  const allDefs: IActionDefinitionDto[] = getActionDefinitions(sd);
  if (!dict || allDefs.length === 0) return undefined;

  let di: IDisplayItem | undefined = dict[Number(hostKuikaId)];
  let guard: number = 0;
  // Walk up the parent chain to the first ancestor whose dataSource/options
  // symbol is bound to a datasource action — that's the enclosing repeater that
  // supplies the row context the `current.<field>` source resolves against.
  while (di && guard < 100) {
    guard += 1;
    const dsSymbol: ISymbol | undefined = di.item?.properties?.find(
      (p: IDisplayItemProperty) =>
        (p.name === "dataSource" || p.name === "options") &&
        (p.symbol?.actionDefinitionId != null ||
          p.symbol?.actionDefinitionObjectPath != null ||
          p.symbol?.resultActionId != null)
    )?.symbol;
    if (dsSymbol) {
      // The datasource action can be referenced by GUID (`actionDefinitionId`)
      // OR — when the binding was set by NAME (the ui_render translator's
      // `set_datasource_bindings` → `setDesignerPropertyDataAction`) — only by
      // `actionDefinitionObjectPath`. That setter stamps the objectPath
      // unconditionally but leaves `actionDefinitionId` undefined whenever the
      // action def isn't resolvable at write time, so a datasource-bound Table
      // routinely carries the name but not the id. Resolve by id first, then by
      // name (strip any `#tag` suffix) — mirroring the cascader engine's
      // multi-key lookup. Without the name fallback the row-context type stayed
      // hardcoded "String" even on a properly datasource-bound repeater.
      let def: IActionDefinitionDto | undefined;
      if (dsSymbol.actionDefinitionId != null) {
        def = allDefs.find((d) => d.id === dsSymbol.actionDefinitionId);
      }
      if (!def && dsSymbol.actionDefinitionObjectPath != null) {
        const actionName: string = String(dsSymbol.actionDefinitionObjectPath).split("#")[0];
        def = allDefs.find((d) => d.name === actionName);
      }
      const element: IKTypeField | undefined = def?.returnTypeModel?.elements?.find((e) => e.name === fieldName);
      if (element?.type?.name) return element.type.name;
    }
    const parentId: number | undefined | null = di.parentId;
    di = parentId !== undefined && parentId !== null ? dict[Number(parentId)] : undefined;
  }
  return undefined;
}

/**
 * KOA-17717 — Enrich a freshly-built `current.<field>` row-context symbol so it
 * matches what the manual designer cascader produces: canonical
 * label/description plus the REAL field type (currentType/targetType, and a
 * converterOfType when the parameter's declared type differs), instead of the
 * hardcoded "String" the deterministic promotion emits.
 *
 * Idempotent and conservative: only touches `category === current` symbols that
 * carry a `fieldName`. When the enclosing repeater / field type can't be
 * resolved it leaves the type untouched (keeps whatever the promotion set), so
 * an unresolvable host is a no-op, never a crash or a blank binding. Called at
 * the two caller seams that build current symbols
 * (`set_multiple_parameter_bindings` and `applyTargetScreenInputParams` in
 * action-tools.ts) so the shared `buildDesignerSymbol` factory and its
 * `IBuildDesignerSymbolInput` contract stay untouched.
 */
export function enrichCurrentRowSymbol(
  symbol: ISymbol,
  sdInstance: ScreenDesignInstance,
  hostKuikaId: string | number | undefined | null,
  paramDeclaredType?: string,
  screenId?: string | null
): ISymbol {
  if (symbol?.category !== ESymbolCategory.current || !symbol.fieldName) return symbol;
  // Cosmetic parity with the manual cascader (codegen consumes
  // FieldName/Category/CurrentType, not these — but the property panel shows
  // them, which is what flagged the bug).
  symbol.label = "Current Row";
  symbol.description = `Current Row.${symbol.fieldName}`;
  // RUNTIME-CRITICAL — codegen resolves the row value via symbol.diId
  // (SymbolModel.DisplayItemId): ClientCodeGenerationBase's `ESymbolCategory.Current`
  // case looks up the row component by diId, walks to the enclosing repeater, and
  // emits `row.<field>`. Without diId it returns `this.Null`, so the navigate
  // screen-input lands EMPTY (`convertToTypeByName(null, …)` — "id boş gidiyor").
  // The deterministic promotion (Fix #42 / #48) never set it; the manual cascader
  // does (`symbol.diId = node.diId`). Mirror it with the host (the row component
  // the binding is attached to). Set BEFORE the field-type resolution's early
  // return so an unresolved type still gets a working row reference.
  const hostIdNum: number = Number(hostKuikaId);
  if (Number.isFinite(hostIdNum)) {
    symbol.diId = hostIdNum;
  }
  const fieldType: string | undefined = resolveCurrentRowFieldType(sdInstance, hostKuikaId, symbol.fieldName, screenId);
  if (!fieldType) return symbol; // fallback — keep the promotion's type, no regression
  symbol.currentType = fieldType as BackEndValueType;
  symbol.targetType = fieldType as BackEndValueType;
  if (paramDeclaredType && paramDeclaredType.toLowerCase() !== fieldType.toLowerCase()) {
    const conversions: CascaderOptionType[] = ScreenDesignHelper.generateTypeConversion(fieldType, paramDeclaredType);
    const converter: string | undefined = conversions[0]?.typeConversion;
    if (converter) symbol.converterOfType = converter;
  }
  return symbol;
}

/**
 * KOA-17797 — Enrich a table-column display binding so the AI/MCP datasource-
 * binding path emits the SAME field-of-type symbol the human designer's column
 * picker produces (`applyTableFieldBinding` in `designer-property-data-editor-actions.ts`).
 * The translator's `set_datasource_bindings` path builds a flat `actionField`
 * symbol carrying only the raw `fieldName` (often mis-cased, `description` =
 * "[Action].Field") and NONE of `fieldToDisplay` / `fieldType` / `fixedValue`
 * ("[datafield:...]") / `tableLabelSourceType`, so a bound column diverges from a
 * hand-bound one and codegen loses the field reference.
 *
 * Resolves the field against the enclosing table's datasource action via the SAME
 * `getTableActionFieldsByKuikaId` the picker uses, so `fieldToDisplay`/`fieldType`
 * are byte-identical and the field casing is corrected (`UserName` -> `Username`),
 * then stamps the shared `buildTableFieldBindingSymbolFields`.
 *
 * Conservative: only touches `category === actionField` symbols with a `fieldName`;
 * when the enclosing table / field can't be resolved it returns the SAME symbol
 * reference unchanged (no regression, no blank binding). On a resolvable symbol it
 * returns a fresh enriched clone. Called at the `set_datasource_bindings` handler
 * seam (property-tools.ts) so the shared `buildDesignerSymbol` factory stays
 * untouched — mirroring `enrichCurrentRowSymbol`.
 */
export function enrichTableFieldBindingSymbol(
  symbol: ISymbol,
  sdInstance: ScreenDesignInstance,
  hostKuikaId: string | number | undefined | null,
  screenId?: string | null
): ISymbol {
  if (symbol?.category !== ESymbolCategory.actionField || !symbol.fieldName) return symbol;
  if (hostKuikaId === undefined || hostKuikaId === null) return symbol;
  const sd: IScreenDesignContextState = resolveEditTarget(sdInstance, screenId);
  const propertySdHelper: PropertySdHelper = new PropertySdHelper(() => sd);
  const fields: IKTypeField[] = propertySdHelper.getTableActionFieldsByKuikaId(String(hostKuikaId), false) ?? [];
  if (fields.length === 0) return symbol;
  const requested: string = String(symbol.fieldName);
  const requestedLower: string = requested.toLowerCase();
  const field: IKTypeField | undefined = fields.find((f: IKTypeField): boolean => {
    const fieldPath: string = f.prefix ? `${f.prefix}.${f.name}` : f.name;
    return fieldPath === requested || f.name === requested || f.name?.toLowerCase() === requestedLower;
  });
  if (!field) return symbol;
  const enriched: ISymbol = { ...symbol };
  enriched.fieldToDisplay = JSON.stringify(field);
  buildTableFieldBindingSymbolFields(enriched, field, field.prefix);
  enriched.tableLabelSourceType = "tableBinding";
  return enriched;
}

/**
 * KOA-18136 §92 — WHAT TO DO INSTEAD, SAID WHERE THE MISTAKE IS MADE.
 *
 * The refusal above is correct and has always named the alternative — but it ended with "otherwise
 * leave the value unbound", and on a workflow task screen that clause is the wrong door held open.
 * Live `d1635ad0`: the turn aimed eleven read-only labels at the process's parameter ids as if they
 * were screen inputs, was refused eleven times, and answered by re-emitting the same labels with a
 * literal em-dash — an unbound value, exactly as offered, on the screen the assignee opens.
 *
 * A task screen is the one place where the two real sources are BOTH non-obvious: it declares no
 * screen inputs at all, and what it does carry is the running process. So there the remedy is
 * named — the read the screen can call, and the process-parameter binding for an action's own
 * parameter — and the "leave it unbound" door is not offered. Everywhere else the sentence is
 * unchanged: on an ordinary screen an unbound value is a legitimate outcome.
 */
function screenInputRefusalRemedy(sd: IScreenDesignContextState): string {
  const onTaskScreen: boolean = findManagedTaskScreenLink(sd?.project, sd?.sdId) !== null;
  if (!onTaskScreen) {
    return (
      "If you meant to show a field from an action result, bind to that field instead; otherwise leave " +
      "the value unbound."
    );
  }
  return (
    "This is a workflow TASK screen: it declares no screen inputs, and what it carries is the RUNNING " +
    "PROCESS. To show the request's own data, bind to a field of the read this screen can call — the " +
    "query keyed by the process instance, attached on pageInit — and feed an action's parameter from " +
    'the process itself with { "bindingType": "processParameter", "inputName": "<the parameter\'s ' +
    'inputName>" }. Do not leave the value unbound and do not put a placeholder where the value belongs.'
  );
}

export function buildDesignerSymbol(
  input: IBuildDesignerSymbolInput,
  sdInstance: ScreenDesignInstance
): ISymbol | IErrorResult {
  const sd = resolveEditTarget(sdInstance, input.screenId);
  const { bindingType } = input;

  switch (bindingType) {
    case "default": {
      const defaultName = input.defaultName;
      if (!defaultName) return { error: "defaultName is required for 'default' bindingType" };
      // KOA-17394 (Batch 15, Fix #48) — auto-promote `current.<field>` to
      // a row-scope symbol. The Binder skill prompt (wirer/definition.ts
      // line 273-279) instructs callers to emit row-context bindings as
      // `bindingType: "default", defaultName: "current.<Field>"` —
      // matching the contract the Wirer's inline path already honors via
      // Fix #42 (action-tools.ts:1090-1102). Without this branch, the
      // legacy lookup falls through to DEFAULT_VALUES (no match), then
      // to a literal-name Default symbol, then codegen emits `null` for
      // an unknown DefaultValueName — guaranteed 404 on every row click.
      // The 04:33 PM 2026-05-24 fikir-havuzu trace hit this on the Sil
      // button: Wirer attached Ideas_Delete with inline `current.Id`
      // (Fix #42 promoted correctly), then Binder rebound via
      // `set_multiple_parameter_bindings` with `defaultName: "current.Id"`,
      // overwriting the correct Current symbol with a broken Default.
      // Same regex shape as Fix #42 — narrow (single dot, identifier-only
      // field, no nesting) so unrelated default names cannot collide.
      const currentMatch: RegExpMatchArray | null = defaultName.match(/^current\.([A-Za-z_][A-Za-z0-9_]*)$/);
      if (currentMatch) {
        const fieldName: string = currentMatch[1];
        return {
          category: ESymbolCategory.current,
          label: fieldName,
          fieldName,
          description: `current.${fieldName}`,
          currentType: "String",
          targetType: "String"
        } as ISymbol;
      }
      // KOA-17564 — auto-promote `screenInput:<name>` (and the dot form) to
      // a proper screen-input symbol, mirroring the `current.<field>`
      // promotion above. The mcp-chat-server Binder's Flow A routes a
      // screenInput source through `bindingType: "default"` with
      // `defaultName: "screenInput:Id"` (projectBindingSource + the projector
      // default branch keep the prefixed form, intending it be resolved HERE
      // "by category" — exactly like `current.X`). Without this branch the
      // lookup misses DEFAULT_VALUES and a literal Default symbol with
      // `defaultValueName: "screenInput:Id"` is produced — a broken binding
      // that the symbol picker never creates. The pageInit-only inbound pass
      // (`applyInboundScreenInputs`) salvages GetById params, but a
      // non-pageInit mutation parameter (e.g. `Masraflar_Update.Id`) stayed
      // mis-bound. Accept both the colon (canonical) and dot forms the
      // platform tolerates; require a single identifier segment so unrelated
      // default names cannot collide.
      // KOA-18136 §105 — a GUID is a VALUE, never the name of a default.
      //
      // The registry names its defaults ("DateNow", "Empty", "CurrentUserid"); nothing in it is
      // named by a guid, and the fall-through below turns an unrecognized name into a literal
      // Default symbol that codegen emits as `null` (the same trap the two promotions above exist
      // for). Live `545b48f8`: both navigate-target repairs resolved the right screen and wrote it
      // as `defaultName: "<screen guid>"`, so three buttons showed an empty target and the runtime
      // would have navigated nowhere. A caller that hands over an id is stating a VALUE, so it is
      // stored as one — the channel `toScreenId`, and every other id-valued parameter, is actually
      // read from.
      const guidValueMatch: RegExpMatchArray | null = defaultName
        .trim()
        .match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);
      if (guidValueMatch) {
        const idValue: string = defaultName.trim();
        return {
          category: ESymbolCategory.fixed,
          label: idValue,
          fixedValue: idValue,
          description: idValue,
          currentType: "Guid",
          targetType: "Guid"
        } as ISymbol;
      }
      const screenInputMatch: RegExpMatchArray | null = defaultName.match(/^screenInput[:.]([A-Za-z_][A-Za-z0-9_]*)$/);
      if (screenInputMatch) {
        const inputName: string = screenInputMatch[1];
        const matchedInput = getScreenInputs(sd).find((item: IScreenInput) => item.name === inputName);
        // Reject an UNDECLARED screen input — parity with the `bindingType:
        // "screenInput"` branch below, which has rejected this since the phantom
        // `{screenInput:userName}` incident. This branch used to tolerate it on
        // purpose ("the Binder synthesizes screenInput sources before the inbound
        // pass declares them"), and that exemption became the one open door for a
        // whole class of dead bindings: a live build bound EIGHT mutation
        // parameters (Employee, Category, Description, ExpenseDate, ExpenseId …)
        // to `screenInput:<FormFieldName>` on a create form that declares no such
        // inputs, every call answered `success: true`, and the user later saw
        // "16 unset parameter(s)" from the backend validator. A form field's value
        // is `componentValue` off its own component; `screenInput:` is only for a
        // real route parameter this screen receives. Tolerating the difference
        // cannot help — an input that is never declared can never resolve.
        //
        // The original exemption assumed the Binder synthesizes screenInput
        // sources BEFORE the inbound pass declares them. That ordering no longer
        // holds: `applyInboundScreenInputs`' CREATE half runs EARLY, before the
        // per-screen build loop, so every legitimately planned input is already
        // declared by the time the Binder binds. A genuinely new one can still be
        // added with `ui_screen_input.create` — which the message asks for.
        if (!matchedInput) {
          const declared: string = getScreenInputs(sd)
            .map((item: IScreenInput) => item.name)
            .join(", ");
          return {
            error:
              `[Screen-input guard] "${defaultName}" — this screen declares no screen input named "${inputName}". ` +
              `Declared inputs: ${declared.length > 0 ? declared : "(none)"}. ` +
              `Screen inputs are values RECEIVED from a navigating caller (e.g. the row id a detail screen is opened with). ` +
              `To read a FORM FIELD on this screen use bindingType "componentValue" with the field's kuikaId; ` +
              `to read another action's output use "actionField". If this screen really should receive "${inputName}", ` +
              `declare it first with ui_screen_input.create.`
          };
        }
        return {
          category: ESymbolCategory.screenInput,
          label: inputName,
          inputName,
          description: inputName,
          currentType: matchedInput.type || "String",
          targetType: matchedInput.type || "String",
          cardinality: matchedInput.cardinality ?? 1
        } as ISymbol;
      }
      // An unrecognized name used to fall through to a literal Default symbol —
      // `success: true` for a binding codegen emits as plain text / `null`. It is
      // an error now, and the error names every valid default.
      const resolvedDefault: IResolvedDefaultValue | IErrorResult = resolveDefaultValueName(defaultName);
      if ("error" in resolvedDefault) return { error: resolvedDefault.error };
      // Which spelling is persisted (registry name vs label) is decided in
      // `resolveDefaultValueName` — see the KOA-17362 note there.
      return {
        category: ESymbolCategory.default,
        label: resolvedDefault.label,
        defaultValueName: resolvedDefault.defaultValueName,
        description: resolvedDefault.label,
        currentType: resolvedDefault.type,
        targetType: resolvedDefault.type
      } as ISymbol;
    }

    case "screenInput": {
      const inputName = input.inputName;
      if (!inputName) return { error: "inputName is required for 'screenInput' bindingType" };
      const screenInput = getScreenInputs(sd).find((item: IScreenInput) => item.name === inputName);
      // Reject a binding to a screen input that is NOT declared on this screen.
      // The Designer occasionally invents `{screenInput:<field>}` in ui_render
      // markup when it should bind a Label to an action-result field — session
      // 91e01b50 bound a HomePage Label to a phantom `screenInput:userName` the
      // user never created. A screen-input binding is valid ONLY for a real route
      // parameter (declared on the screen); an undeclared one ships a broken
      // binding the user didn't ask for. This is the translator/Designer path
      // (`bindingType:"screenInput"`); the Binder's legitimate screenInput
      // synthesis routes through the `bindingType:"default"` promotion above,
      // which deliberately tolerates not-yet-declared inputs — so rejecting here
      // does not touch that flow.
      if (!screenInput) {
        const declaredInputs: string = getScreenInputs(sd)
          .map((item: IScreenInput) => item.name)
          .join(", ");
        return {
          error:
            `Screen input "${inputName}" is not declared on this screen — cannot bind to a non-existent ` +
            `screen input. Declared screen inputs: ${declaredInputs.length > 0 ? declaredInputs : "(none)"}. ` +
            screenInputRefusalRemedy(sd)
        };
      }
      return {
        category: ESymbolCategory.screenInput,
        label: inputName,
        inputName,
        description: inputName,
        currentType: screenInput.type || "String",
        targetType: screenInput.type || "String",
        cardinality: screenInput.cardinality ?? 1
      } as ISymbol;
    }

    case "componentValue": {
      const sourceKuikaId = input.sourceKuikaId;
      const sourcePropertyName = input.sourcePropertyName;
      if (!sourceKuikaId) return { error: "sourceKuikaId is required for 'componentValue' bindingType" };
      if (!sourcePropertyName) return { error: "sourcePropertyName is required for 'componentValue' bindingType" };

      const sourceDi = getScreenDictionary(sd)[parseInt(sourceKuikaId, 10)];
      let resolvedType = "String";
      if (sourceDi?.item?.properties) {
        const sourceProperty = sourceDi.item.properties.find(
          (property: IDisplayItemProperty) => property.name === sourcePropertyName
        );
        if (sourceProperty?.symbol?.currentType) {
          resolvedType = sourceProperty.symbol.currentType;
        }
      }

      return {
        category: ESymbolCategory.displayItemProp,
        label: sourcePropertyName,
        diPropName: sourcePropertyName,
        diId: parseInt(sourceKuikaId, 10),
        description: `[diId:${sourceKuikaId}].${sourcePropertyName}`,
        currentType: resolvedType,
        targetType: resolvedType
      } as ISymbol;
    }

    case "actionResult": {
      // KOA-17797 — read `sourceActionId` FIRST (mirroring the actionField case
      // below). In a parameter binding, `input.actionId` is consumed upstream by
      // getResolvedActionParameter to locate the TARGET action whose param is set;
      // if `actionResult` also read `actionId` as its SOURCE the two collided and a
      // whole/scalar result (e.g. PrintPdf.base64 <- ComponentToPdf) could not name
      // a source distinct from its target. `sourceActionId` carries the SOURCE.
      const actionId = input.sourceActionId ?? input.actionId;
      if (!actionId) return { error: "sourceActionId or actionId is required for 'actionResult' bindingType" };
      const allDefs = getActionDefinitions(sd);
      const screenDesign = getScreenDesign(sd);
      const allActions = screenDesign?.displayItems ? collectActionsFromAllDIs(screenDesign.displayItems) : [];
      const actionInstance = allActions.find((action) => action.actionId === actionId);
      let actionDef: IActionDefinitionDto | undefined;

      if (actionInstance?.actionDefinitionId) {
        actionDef = allDefs.find((definition) => definition.id === actionInstance.actionDefinitionId);
      }
      if (!actionDef) {
        actionDef = allDefs.find((definition) => definition.id === actionId);
      }

      return {
        category: ESymbolCategory.actionResult,
        label: actionDef?.name || actionInstance?.label || "Action Result",
        actionDefinitionId: actionDef?.id || actionInstance?.actionDefinitionId,
        description: `[${actionDef?.name || actionInstance?.label || actionId}]`,
        currentType: actionDef?.returnTypeName || actionInstance?.returnType || "Object",
        targetType: actionDef?.returnTypeName || actionInstance?.returnType || "Object",
        resultActionId: actionId,
        cardinality: actionDef?.returnCardinality ?? actionInstance?.returnCardinality ?? (2 as ECardinality)
      } as ISymbol;
    }

    case "actionField": {
      const sourceActionId = input.sourceActionId;
      const fieldName = input.fieldName;
      if (!fieldName) return { error: "fieldName is required for 'actionField' bindingType" };

      const allDefs = getActionDefinitions(sd);
      const screenDesign = getScreenDesign(sd);
      const allActions = screenDesign?.displayItems ? collectActionsFromAllDIs(screenDesign.displayItems) : [];

      if (sourceActionId) {
        // Detail/edit screen: bind to a specific action's result field (e.g. GetById.first.Id)
        const sourceInstance = allActions.find((a) => a.actionId === sourceActionId);
        let sourceDef: IActionDefinitionDto | undefined;
        if (sourceInstance?.actionDefinitionId) {
          sourceDef = allDefs.find((d) => d.id === sourceInstance.actionDefinitionId);
        }
        if (!sourceDef) {
          sourceDef = allDefs.find((d) => d.id === sourceActionId);
        }

        // Resolve field type from the action definition's returnTypeModel
        let resolvedType: string = "String";
        if (sourceDef?.returnTypeModel?.elements) {
          const element = sourceDef.returnTypeModel.elements.find((e) => e.name === fieldName);
          if (element?.type?.name) {
            resolvedType = element.type.name;
          }
        }

        const actionName = sourceDef?.name || sourceInstance?.label || sourceActionId;
        return {
          category: ESymbolCategory.fieldOfType,
          label: "Action Result",
          fieldName,
          description: `[${actionName}].first.${fieldName}`,
          actionDefinitionId: sourceDef?.id || sourceInstance?.actionDefinitionId,
          resultActionId: sourceActionId,
          currentType: resolvedType,
          targetType: resolvedType,
          cardinality: ECardinality.One,
          converterOfCardinality: 0
        } as ISymbol;
      }

      // Table row context: bind to current row's field from the parent datasource
      const actionId = input.actionId;
      if (!actionId) return { error: "actionId or sourceActionId is required for 'actionField' bindingType" };

      // Try to resolve the action name and field type for a better symbol
      const actionInstance = allActions.find((a) => a.actionId === actionId);
      let actionDef: IActionDefinitionDto | undefined;
      if (actionInstance?.actionDefinitionId) {
        actionDef = allDefs.find((d) => d.id === actionInstance.actionDefinitionId);
      }
      if (!actionDef) {
        actionDef = allDefs.find((d) => d.id === actionId);
      }

      let resolvedType: string = "String";
      if (actionDef?.returnTypeModel?.elements) {
        const element = actionDef.returnTypeModel.elements.find((e) => e.name === fieldName);
        if (element?.type?.name) {
          resolvedType = element.type.name;
        }
      }

      const label = actionDef?.name || actionInstance?.label || actionId;
      return {
        category: ESymbolCategory.actionField,
        label: fieldName,
        fieldName,
        description: `[${label}].${fieldName}`,
        currentType: resolvedType,
        targetType: resolvedType
      } as ISymbol;
    }

    case "fixed":
    case "constant": {
      // Literal value (e.g. a confirmation/dialog message authored by the
      // Validation Fixer, or any constant the Binder projects). The Binder's
      // `constant` BindingType is dispatched here as `bindingType: "fixed"`.
      const fixedValue = input.fixedValue;
      if (fixedValue === undefined || fixedValue === null || fixedValue === "") {
        return { error: "fixedValue is required for 'fixed' bindingType" };
      }
      return getFixedSymbol(fixedValue, fixedValue, fixedValue);
    }

    case "processParameter": {
      // KOA-18136 — a value the RUNNING PROCESS carries into a platform-managed
      // workflow task screen: the process instance id, the step instance id,
      // who started it, or any of the workflow's own parameters. The platform
      // loads that bag at the head of the screen's pageInit, and the symbol
      // picker has always offered it under "Process → Common / Business Object
      // / Internal" — but only through the cascader UI. Without this branch no
      // headless caller could bind to it, which is why an action attached to a
      // task screen shipped with empty parameters (live 2026-08-11: a correct
      // "previous requests of this requester" query called with `""`).
      const sourceKey: string | undefined = input.inputName ?? input.fieldName;
      if (typeof sourceKey !== "string" || sourceKey.trim().length === 0) {
        return { error: "inputName is required for 'processParameter' bindingType" };
      }
      const bagContext = managedBindingContextFromSdState(sd);
      const source = findManagedScreenBindingSource(bagContext, sourceKey);
      if (!source) {
        // Fail closed, and say which of the two reasons applies — on an
        // ordinary screen the process bag does not exist at runtime, so a
        // tolerated binding here would be dead on arrival.
        const available = listManagedScreenBindingSources(bagContext);
        if (available.length === 0) {
          return {
            error:
              `[Process-binding guard] "${sourceKey}" — this screen is not a platform-managed workflow task screen, ` +
              `so it carries no process context to read. Process values (ProcessInstanceId, StepInstanceId, the ` +
              `process initiator, the workflow's own parameters) exist only on a screen generated for a workflow ` +
              `user task. On an ordinary screen bind to a screen input, a component value, or another action's result.`
          };
        }
        return {
          error:
            `[Process-binding guard] "${sourceKey}" is not a value this managed task screen carries. ` +
            `Available: ${available.map((s) => `${s.label} (${s.key})`).join(", ")}.`
        };
      }
      return buildManagedScreenBindingSymbol(source, input.targetType);
    }

    default:
      return {
        error: `Unknown bindingType: "${bindingType}". Use: default, screenInput, componentValue, actionResult, actionField, fixed, processParameter`
      };
  }
}

export function setDesignerPropertyValue(
  sdInstance: ScreenDesignInstance,
  input: ISetPropertyValueInput,
  changeReason: ESdChangeReason = ESdChangeReason.ChangePropertyValue
): ISuccessResult | IErrorResult {
  const { kuikaId, propertyName, value } = input;
  if (!kuikaId || !propertyName) {
    return { error: "kuikaId and propertyName are required" };
  }

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const lookup = validateKuikaId(sd, kuikaId);
  if ("error" in lookup) return { success: false, ...lookup };

  const propError = validatePropertyName(sd, lookup.di, propertyName);
  if (propError) return { success: false, ...propError };

  const dataEditorProp = isDataEditorProperty(sd, lookup.di, propertyName);
  if (dataEditorProp) {
    return {
      success: false,
      error: `Property "${propertyName}" is a data-editor property and cannot be set as a simple value. Use ui_logic_set_property_data_action and ui_logic_set_property_data_field instead.`
    };
  }

  applyDesignerPropertyChange(sdInstance, { propertyName, value, kuikaId }, changeReason, input.screenId);
  return { success: true, message: `Set ${propertyName} = ${value} on ${kuikaId}` };
}

/**
 * Writes a property's symbol in ONE commit — together with the Label formatter it may derive, so the
 * pair is a single undo step.
 *
 * The commit never moves the selection, exactly like `setDesignerPropertyValue`. The component whose
 * property is written is not always the selected one: the Navigate action panel edits its
 * Drawer/Modal/FloatPanel while the component that owns the action stays selected, and selecting the
 * written component there threw the user out of the panel they were editing (KOA-17203).
 */
export function setDesignerPropertySymbol(
  sdInstance: ScreenDesignInstance,
  input: ISetPropertySymbolInput,
  changeReason: ESdChangeReason = ESdChangeReason.ChangedCustomFieldOnPropertyPanel
): ISuccessResult | IErrorResult {
  const { kuikaId, propertyName, symbol } = input;
  if (!kuikaId || !propertyName || !symbol) {
    return { error: "kuikaId, propertyName, and symbol are required" };
  }

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const lookup = validateKuikaId(sd, kuikaId);
  if ("error" in lookup) return { success: false, ...lookup };

  const propError = validatePropertyName(sd, lookup.di, propertyName);
  if (propError && !shouldAllowDynamicPropertyCreation(lookup.di, propertyName, input.allowDynamicProperty)) {
    return { success: false, ...propError };
  }

  const didChange = applyPropertyGridChangeWithoutCommit(
    sd,
    { propertyName, kuikaId, symbol, value: undefined },
    changeReason
  );
  const didApplySideEffects = applyDesignerPropertySymbolSideEffects(sd, kuikaId, propertyName, symbol, false);

  if (didChange || didApplySideEffects) {
    setScreenDesign(sd, getScreenDesign(sd), changeReason);
  }

  return {
    success: true,
    message: `Updated symbol for ${propertyName} on ${kuikaId}`
  };
}

export function updateDesignerPropertySymbol(
  sdInstance: ScreenDesignInstance,
  input: IUpdatePropertySymbolInput,
  changeReason: ESdChangeReason = ESdChangeReason.ChangedCustomFieldOnPropertyPanel
): ISuccessResult | IErrorResult {
  const { kuikaId, propertyName } = input;
  const symbolPatch = safeParseJsonish(input.symbolPatch ?? input.patch);
  if (!kuikaId || !propertyName || !symbolPatch || typeof symbolPatch !== "object") {
    return { error: "kuikaId, propertyName, and symbolPatch object are required" };
  }

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const lookup = validateKuikaId(sd, kuikaId);
  if ("error" in lookup) return { success: false, ...lookup };

  const propError = validatePropertyName(sd, lookup.di, propertyName);
  if (propError) return { success: false, ...propError };

  const property = getResolvedPropertyModel(sd, lookup.di, propertyName);
  const nextSymbol: ISymbol = {
    ...(property?.symbol || {}),
    ...(symbolPatch as Partial<ISymbol>)
  } as ISymbol;

  return setDesignerPropertySymbol(
    sdInstance,
    { kuikaId, propertyName, symbol: nextSymbol, screenId: input.screenId },
    changeReason
  );
}

export function setDesignerPropertyType(
  sdInstance: ScreenDesignInstance,
  input: ISetPropertyTypeInput
): ISuccessResult | IErrorResult {
  const { kuikaId, propertyName, selectedType } = input;
  if (!kuikaId || !propertyName || selectedType == null) {
    return { error: "kuikaId, propertyName, and selectedType are required" };
  }

  const sd = resolveEditTarget(sdInstance, input.screenId);
  changeSelectedPropertyType(sd, {
    propertyName,
    selectedValue: Number(selectedType),
    kuikaId
  });

  return {
    success: true,
    message: `Changed property type for ${propertyName} on ${kuikaId}`
  };
}

export function setDesignerPropertyTypeOptions(
  sdInstance: ScreenDesignInstance,
  input: ISetPropertyTypeOptionsInput
): ISuccessResult | IErrorResult {
  const { kuikaId, propertyName, selectedTypeOptions } = input;
  if (!kuikaId || !propertyName || selectedTypeOptions === undefined) {
    return { error: "kuikaId, propertyName, and selectedTypeOptions are required" };
  }

  const sd = resolveEditTarget(sdInstance, input.screenId);
  changeSelectedPropertyType(sd, {
    propertyName,
    selectedTypeOptions: safeParseJsonish(
      selectedTypeOptions
    ) as IChangeSelectedPropertyTypeParameters["selectedTypeOptions"],
    kuikaId
  });

  return {
    success: true,
    message: `Changed property type options for ${propertyName} on ${kuikaId}`
  };
}

export function setMultipleDesignerProperties(
  sdInstance: ScreenDesignInstance,
  kuikaId: string,
  properties: IPropertyEntry[],
  screenId?: string | null
): ISuccessResultWithSkipped | IErrorResult {
  if (!kuikaId || !properties || !Array.isArray(properties)) {
    return { error: "kuikaId and properties array are required" };
  }

  const sd = resolveEditTarget(sdInstance, screenId);
  const lookup = validateKuikaId(sd, kuikaId);
  if ("error" in lookup) return { success: false, ...lookup };

  const results: string[] = [];
  const skipped: string[] = [];

  for (const property of properties) {
    const { propertyName, value } = property;
    if (!propertyName || value === undefined) continue;

    const propError = validatePropertyName(sd, lookup.di, propertyName);
    if (propError) {
      skipped.push(`${propertyName} (not found)`);
      continue;
    }

    const dataEditorProp = isDataEditorProperty(sd, lookup.di, propertyName);
    if (dataEditorProp) {
      skipped.push(`${propertyName} (data-editor property — use ui_logic_set_property_data_action instead)`);
      continue;
    }

    if (propertyName === "span" && value !== undefined) {
      syncColResponsive(sdInstance, sd, kuikaId, String(value));
    }

    applyPropertyGridChangeWithoutCommit(sd, { propertyName, value, kuikaId }, ESdChangeReason.ChangePropertyValue);
    results.push(`${propertyName} = ${value}`);
  }

  if (results.length > 0) {
    setScreenDesign(sd, getScreenDesign(sd), ESdChangeReason.ChangePropertyValue);
  }

  const response: ISuccessResultWithSkipped = {
    success: true,
    message: `Set ${results.length} properties on ${kuikaId}: ${results.join(", ")}`
  };

  if (skipped.length > 0) {
    response.skipped = skipped;
  }

  return response;
}

interface IMicroAppProjectListResult {
  success: boolean;
  projects?: { id: string; name: string }[];
  error?: string;
}

export async function listDesignerMicroAppProjects(
  sdInstance: ScreenDesignInstance
): Promise<IMicroAppProjectListResult> {
  const sd = getValues(sdInstance);

  if (Array.isArray(sd.projects) && sd.projects.length > 0) {
    return {
      success: true,
      projects: sd.projects.map((project) => ({
        id: project.id,
        name: project.name
      }))
    };
  }

  try {
    sd?.setProjectsLoadingStatus?.(true, false);
    const response = await ProjectService.GetAllProjectNames();
    if (response?.status !== 200) {
      return { success: false, error: "Failed to load projects" };
    }

    const projects = (response.data || []) as { id: string; name: string }[];
    if (typeof sd?.setProjects === "function") {
      await new Promise<void>((resolve) => {
        sd.setProjects(projects as { id: string; name: string; schemaName: string }[], () => resolve());
      });
    }
    sd?.setProjectsLoadingStatus?.(false, true);

    return {
      success: true,
      projects: projects.map((project) => ({
        id: project.id,
        name: project.name
      }))
    };
  } catch (error: unknown) {
    sd?.setProjectsLoadingStatus?.(false, sd?.isProjectsLoaded);
    const message = error instanceof Error ? error.message : "Failed to load projects";
    return { success: false, error: message };
  } finally {
    sd?.setProjectsLoadingStatus?.(false, sd?.isProjectsLoaded);
  }
}

interface IMicroAppScreenListResult {
  success: boolean;
  projectId?: string;
  screens?: { id: string; name: string }[];
  options?: { value: string; label: string }[];
  error?: string;
}

export async function listDesignerMicroAppProjectScreens(
  sdInstance: ScreenDesignInstance,
  input: IMicroAppProjectInput
): Promise<IMicroAppScreenListResult> {
  const projectId = input?.projectId;
  if (!projectId) {
    return { success: false, error: "projectId is required" };
  }

  try {
    const response = await ScreenService.GetScreenNamesByProjectId(projectId);
    if (response?.status !== 200) {
      return { success: false, error: `Failed to load screens for project "${projectId}"` };
    }

    const screens = (response.data || []) as { id: string; name: string }[];
    setScreensDataInActiveIframe(projectId, screens);

    return {
      success: true,
      projectId,
      screens,
      options: screens.map((screen) => ({
        value: screen.id,
        label: screen.name
      }))
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : `Failed to load screens for project "${projectId}"`;
    return { success: false, error: message };
  }
}

interface IMicroAppScreenInputsResult {
  success: boolean;
  screenId?: string;
  inputs?: { name: string; typeName: string; cardinality: number }[];
  error?: string;
}

export async function listDesignerMicroAppScreenInputs(
  sdInstance: ScreenDesignInstance,
  input: IMicroAppScreenInputsInput
): Promise<IMicroAppScreenInputsResult> {
  const screenId = input?.screenId;
  if (!screenId) {
    return { success: false, error: "screenId is required" };
  }

  try {
    const response = await ScreenService.GetScreenInputsByScreenId(screenId);
    if (response?.status !== 200) {
      return { success: false, error: `Failed to load screen inputs for screen "${screenId}"` };
    }

    const inputs = response.data || [];
    const sd = getValues(sdInstance);
    if (input?.kuikaId) {
      const lookup = validateKuikaId(sd, input.kuikaId);
      if ("error" in lookup) return { success: false, ...lookup };

      const existingPropertyNames = new Set(
        (lookup.di?.item?.properties || []).map((property: IDisplayItemProperty) => property.name)
      );
      for (const inputItem of inputs) {
        const inputPropertyName = `screenInput_${inputItem.name}`;
        if (existingPropertyNames.has(inputPropertyName)) {
          continue;
        }

        setDesignerPropertySymbol(
          sdInstance,
          {
            kuikaId: input.kuikaId,
            propertyName: inputPropertyName,
            symbol: getFixedSymbol(undefined, `Value for ${inputItem.name}`, `Value for ${inputItem.name}`),
            allowDynamicProperty: true
          },
          ESdChangeReason.PropertyFixedValueChanged
        );
      }
    }

    return {
      success: true,
      screenId,
      inputs
    };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : `Failed to load screen inputs for screen "${screenId}"`;
    return { success: false, error: message };
  }
}

interface ISetMicroAppProjectResult {
  success: boolean;
  kuikaId?: string;
  projectId?: string;
  screens?: { id: string; name: string }[];
  screenOptions?: { value: string; label: string }[];
  error?: string;
}

export async function setDesignerMicroAppProject(
  sdInstance: ScreenDesignInstance,
  input: ISetMicroAppProjectInput
): Promise<ISetMicroAppProjectResult> {
  const { kuikaId, projectId } = input || {};
  if (!kuikaId) return { success: false, error: "kuikaId is required" };
  if (!projectId) return { success: false, error: "projectId is required" };

  const sd = getValues(sdInstance);
  const lookup = validateKuikaId(sd, kuikaId);
  if ("error" in lookup) return { success: false, ...lookup };

  const projectResult = setDesignerPropertyValue(sdInstance, {
    kuikaId,
    propertyName: input.projectPropertyName || "projectId",
    value: projectId
  });
  if ("error" in projectResult) {
    return { success: false, error: projectResult.error };
  }

  const clearResult = await setDesignerMicroAppStartingScreen(sdInstance, {
    kuikaId,
    screenId: "",
    propertyName: input.startingScreenPropertyName || "startingScreen"
  });
  if ("error" in clearResult && clearResult.error) {
    return { success: false, error: clearResult.error };
  }

  const screensResult = await listDesignerMicroAppProjectScreens(sdInstance, { projectId });
  if (!screensResult.success || screensResult.error) {
    return { success: false, error: screensResult.error };
  }

  return {
    success: true,
    kuikaId,
    projectId,
    screens: screensResult.screens,
    screenOptions: screensResult.options
  };
}

interface ISetMicroAppStartingScreenResult {
  success: boolean;
  kuikaId?: string;
  propertyName?: string;
  screenId?: string;
  inputs?: { name: string; typeName: string; cardinality: number }[];
  error?: string;
}

export async function setDesignerMicroAppStartingScreen(
  sdInstance: ScreenDesignInstance,
  input: ISetMicroAppStartingScreenInput
): Promise<ISetMicroAppStartingScreenResult> {
  const { kuikaId } = input || {};
  const screenId = input?.screenId ?? input?.startingScreenId ?? "";
  const propertyName = input?.propertyName || "startingScreen";
  if (!kuikaId) return { success: false, error: "kuikaId is required" };

  const sd = getValues(sdInstance);
  const lookup = validateKuikaId(sd, kuikaId);
  if ("error" in lookup) return { success: false, ...lookup };

  const di = lookup.di;
  const nextSymbol = getFixedSymbol(screenId || undefined);
  const startingScreenResult = setDesignerPropertySymbol(
    sdInstance,
    {
      kuikaId,
      propertyName,
      symbol: nextSymbol
    },
    ESdChangeReason.PropertyFixedValueChanged
  );
  if ("error" in startingScreenResult) {
    return { success: false, error: startingScreenResult.error };
  }

  for (const property of getMicroAppScreenInputProperties(di)) {
    setDesignerPropertySymbol(
      sdInstance,
      {
        kuikaId,
        propertyName: property.name,
        symbol: getFixedSymbol(undefined, property.symbol?.description || "", property.symbol?.label || ""),
        allowDynamicProperty: true
      },
      ESdChangeReason.PropertyFixedValueChanged
    );
  }

  if (!screenId) {
    return {
      success: true,
      kuikaId,
      propertyName,
      screenId: "",
      inputs: []
    };
  }

  const inputsResult = await listDesignerMicroAppScreenInputs(sdInstance, { screenId, kuikaId });
  if (!inputsResult.success || inputsResult.error) {
    return { success: false, error: inputsResult.error };
  }

  return {
    success: true,
    kuikaId,
    propertyName,
    screenId,
    inputs: inputsResult.inputs || []
  };
}

interface IListBindingOptionsResult {
  success?: false;
  error?: string;
  targetType?: string;
  targetCardinality?: number;
  pickerOptions?: IDesignerSymbolPickerOption[];
  supportsOptionPath?: boolean;
  categories?: ILegacyBindingCategories;
  isInsideDataRepeater?: boolean;
  recommendation?: string;
}

/**
 * When the target component is inside a data repeater (Table, GalleryView, etc.),
 * reorder options so "Current" is first and filter out categories that would produce
 * same-value-for-all-rows bindings. Both list and set binding functions must use
 * this so the serialized paths remain consistent.
 */
function applyDataRepeaterOptionFiltering(
  options: CascaderOptionType[],
  di: IDisplayItem | undefined,
  engine: DesignerSymbolPickerEngine
): CascaderOptionType[] {
  const hasCurrentOptions = options.some((opt) => opt.category === ESymbolCategory.current);
  const isStructurallyInsideDataRepeater = di
    ? engine.getParents(di).some((parent) => engine.isDataBindingOptions(parent))
    : false;

  if (hasCurrentOptions) {
    // "Current" options exist — reorder them first and filter out categories
    // that would produce same-value-for-all-rows bindings.
    const currentOptions = options.filter((opt) => opt.category === ESymbolCategory.current);
    const otherOptions = options.filter(
      (opt) =>
        opt.category !== ESymbolCategory.current &&
        opt.category !== ESymbolCategory.actionResults &&
        opt.category !== ESymbolCategory.actionResult &&
        opt.category !== ESymbolCategory.displayItem
    );
    return [...currentOptions, ...otherOptions];
  }

  if (isStructurallyInsideDataRepeater) {
    // Inside a data repeater but "Current" not generated — datasource action not configured yet.
    // Filter out wrong categories to prevent incorrect same-value-for-all-rows bindings.
    return options.filter(
      (opt) =>
        opt.category !== ESymbolCategory.actionResults &&
        opt.category !== ESymbolCategory.actionResult &&
        opt.category !== ESymbolCategory.displayItem
    );
  }

  return options;
}

export function listDesignerBindingOptions(
  sdInstance: ScreenDesignInstance,
  input: IListBindingOptionsInput
): IListBindingOptionsResult {
  const sd = resolveEditTarget(sdInstance, input.screenId);
  const context = createDesignerSymbolPickerContext(sd, input);
  if ("error" in context) return { success: false, ...context };

  const options = context.engine.generateItems(context.targetType, context.targetCardinality as ECardinality);
  const hasCurrentOptions = options.some((opt) => opt.category === ESymbolCategory.current);

  // Structural detection: check parent hierarchy for data repeater containers.
  // This works even when the Table's datasource action isn't configured yet
  // (in which case generateCurrentRow returns empty children and "Current" is not in options).
  const parents = context.di ? context.engine.getParents(context.di) : [];
  const dataRepeaterParents = parents.filter((parent) => context.engine.isDataBindingOptions(parent));
  const isStructurallyInsideDataRepeater = dataRepeaterParents.length > 0;
  const isInsideDataRepeater = hasCurrentOptions || isStructurallyInsideDataRepeater;

  const effectiveOptions = applyDataRepeaterOptionFiltering(options, context.di, context.engine);

  const categories = buildLegacyBindingCategoriesFromOptions(effectiveOptions);

  // When inside a data repeater with currentRow available, trim verbose categories
  // to reduce token cost (~1000 tokens savings per call). Only currentRow is needed for binding.
  if (isInsideDataRepeater && hasCurrentOptions && categories.currentRow && categories.currentRow.length > 0) {
    const trimmedResult: IListBindingOptionsResult = {
      targetType: context.targetType,
      targetCardinality: context.targetCardinality,
      pickerOptions: [],
      supportsOptionPath: false,
      categories: {
        ...categories,
        defaults: undefined as any,
        screenInputs: undefined as any,
        componentValues: undefined as any,
        actionResults: undefined as any
      }
    };
    trimmedResult.isInsideDataRepeater = true;
    trimmedResult.recommendation =
      "Use ui_property.set_binding with bindingType 'actionField', actionId = pageInit action instance ID, fieldName = field name from currentRow.";
    // Clean up undefined keys
    const cats = trimmedResult.categories as any;
    delete cats.defaults;
    delete cats.screenInputs;
    delete cats.componentValues;
    delete cats.actionResults;
    return trimmedResult;
  }

  const result: IListBindingOptionsResult = {
    targetType: context.targetType,
    targetCardinality: context.targetCardinality,
    pickerOptions: [],
    supportsOptionPath: false,
    categories
  };

  if (isInsideDataRepeater) {
    result.isInsideDataRepeater = true;
    result.recommendation =
      "This component is inside a data repeater, but the parent datasource action is not configured yet. " +
      "First call ui_property.set_data_action on the parent Table/GalleryView, then call list_binding_options again.";
  }

  return result;
}

interface ISetPropertyBindingResult {
  success: boolean;
  optionPath?: number[] | null;
  message?: string;
  error?: string;
}

export function setDesignerPropertyBinding(
  sdInstance: ScreenDesignInstance,
  input: ISetPropertyBindingInput
): ISetPropertyBindingResult | IErrorResult {
  const { kuikaId, propertyName, bindingType } = input;
  if (!kuikaId) return { error: "kuikaId is required" };
  if (!propertyName) return { error: "propertyName is required" };

  const sd = resolveEditTarget(sdInstance, input.screenId);
  const context = createDesignerSymbolPickerContext(sd, input);
  if ("error" in context) return { success: false, ...context };

  const rawOptions = context.engine.generateItems(context.targetType, context.targetCardinality as ECardinality);
  // Apply the same filtering/reordering as list_binding_options so optionPaths stay consistent.
  const options = applyDataRepeaterOptionFiltering(rawOptions, context.di, context.engine);
  const optionPath =
    parseOptionPath(input.optionPath ?? input.pickerPath ?? input.selectedPath) ||
    (bindingType ? resolveLegacyBindingPath(options, input) : null);

  let symbol: ISymbol | IErrorResult | null | undefined;
  if (optionPath) {
    const selectedOptions = getDesignerSymbolPickerSelectedOptionsByPath(options, optionPath);
    if (!selectedOptions) {
      return {
        success: false,
        error: `Binding option path [${optionPath.join(", ")}] is not valid for ${propertyName} on ${kuikaId}`
      };
    }

    symbol = context.engine.buildSymbolFromSelectedOptions(optionPath as CascaderValueType, selectedOptions);
  } else if (bindingType) {
    symbol = buildDesignerSymbol(input as IBuildDesignerSymbolInput, sdInstance);
  } else {
    return {
      error:
        "optionPath is required. Call ui_structure_list_binding_options and use one of the returned pickerOptions paths."
    };
  }

  if (symbol && "error" in symbol) return symbol;
  if (!symbol) {
    return { success: false, error: `Could not resolve binding for ${propertyName} on ${kuikaId}` };
  }

  // Preserve existing fixedValue and category on data-editor-managed properties.
  // In the UI (property-form-item.tsx), these properties go through the data editor flow
  // (getDesignerPropertyDataEditorState().success === true) which clones the existing symbol
  // and only modifies action-related fields — fixedValue (design-time sample data) and
  // category are never overwritten. We mirror that here so MCP tool calls don't wipe them out.
  if (context.di && context.property) {
    const existingProp = context.property as IProperty;
    const existingSymbol = existingProp.symbol;
    if (existingSymbol) {
      let isDataEditorProperty =
        existingProp.isValueProp === true ||
        existingProp.name === "defaultDataField" ||
        existingProp.name === "rowKey" ||
        existingProp.name === "dynamicFontColor" ||
        existingProp.name === "dynamicBackGroundColor" ||
        existingProp.name === "dynamicBgImage";

      if (!isDataEditorProperty && Array.isArray(existingProp.type)) {
        const propertyHelper = new PropertyHelper();
        const toolType = propertyHelper.calculatePropertyToolType(context.di as IDisplayItem, existingProp);
        isDataEditorProperty = DATA_EDITOR_TOOL_TYPES.has(toolType);
      }

      if (isDataEditorProperty) {
        const newSymbol = symbol as ISymbol;
        if (existingSymbol.fixedValue !== undefined && existingSymbol.fixedValue !== "" && !newSymbol.fixedValue) {
          newSymbol.fixedValue = existingSymbol.fixedValue;
        }
        if (existingSymbol.category !== undefined && newSymbol.category === undefined) {
          newSymbol.category = existingSymbol.category;
        }
      }
    }
  }

  const result = setDesignerPropertySymbol(sdInstance, { propertyName, kuikaId, symbol: symbol as ISymbol });
  if ("error" in result) {
    return result;
  }

  return {
    success: true,
    optionPath,
    message: `Bound ${propertyName} on ${kuikaId} to ${(symbol as ISymbol).description || (symbol as ISymbol).label}`
  };
}

/* -------------------------------------------------------------------------- *
 *  Bindables manifest                                                         *
 *                                                                             *
 *  Returns, per requested component, the set of symbols the cascader would    *
 *  show for each bindable property. Consumed by `ui_render`'s response so an  *
 *  LLM can pick `ui_bind` refs without a separate discovery call.             *
 * -------------------------------------------------------------------------- */

export interface IBindablesManifestTarget {
  kuikaId: string;
  propertyNames?: string[];
  eventNames?: string[];
}

export interface IBindablesManifestInput extends IScreenScopedInput, SlotNarrowing {
  targets: IBindablesManifestTarget[];
}

export interface IBindablesManifestResult {
  success: true;
  manifest: BindablesManifest;
  skipped?: { kuikaId: string; error: string }[];
  /** Present only when `maxOptionsPerCategory` left options out — how many, and how to get them. */
  truncationNote?: string;
}

/**
 * Validate the optional narrowing params. An unknown category is an ERROR, not
 * an empty manifest: "no options" and "you misspelled the filter" must never
 * look the same to the caller.
 */
export function validateBindablesNarrowing(narrowing: SlotNarrowing): IErrorResult | null {
  const unknown: string[] = (narrowing.categories ?? []).filter(
    (category: string): boolean =>
      !MANIFEST_BINDING_CATEGORIES.some(
        (known: string): boolean => typeof category === "string" && known.toLowerCase() === category.toLowerCase()
      )
  );
  if (unknown.length > 0) {
    return {
      error: `Unknown bindable categories: ${unknown.join(", ")}. Valid categories: ${MANIFEST_BINDING_CATEGORIES.join(", ")}.`
    };
  }
  const limit: number | undefined = narrowing.maxOptionsPerCategory;
  if (limit !== undefined && (!Number.isInteger(limit) || limit < 1)) {
    return { error: "maxOptionsPerCategory must be a positive integer." };
  }
  return null;
}

const getPlatformContext = (sd: IScreenDesignContextState): AttachablePlatformContext => {
  const project = sd.project;
  return {
    projectType: project.projectType,
    isWebScreen: sd.getIsWebScreen(),
    dashboardState: sd.dashboardState,
    isOfflineScreen: project.offlineScreenId === KMainFunctions.getSdIdFromUrl()
  };
};

const listBindableValueProperties = (sd: IScreenDesignContextState, di: IDisplayItem): string[] => {
  const validNames = getValidPropertyNames(sd, di);
  return Array.from(validNames);
};

const listBindableEventNames = (di: IDisplayItem): string[] => {
  const properties = di.item?.properties;
  if (!Array.isArray(properties)) return [];
  return properties.filter((p) => p.selectedType === 3).map((p) => p.name);
};

const buildValueSlot = (
  sd: IScreenDesignContextState,
  kuikaId: string,
  propertyName: string,
  slotCache: Map<string, BindablePropertySlot>
): BindablePropertySlot | null => {
  try {
    const context = createDesignerSymbolPickerContext(sd, { kuikaId, propertyName });
    if ("error" in context) return null;
    // A single component's ~10 value properties typically collapse to 2–3
    // distinct (targetType, targetCardinality) signatures — String/One dominates.
    // Memoize the filtered cascader per signature so we skip redundant walks of
    // the full screen tree inside `generateItems` for sibling properties.
    const cacheKey = `${kuikaId}|${context.targetType}|${context.targetCardinality}`;
    const cached = slotCache.get(cacheKey);
    if (cached) return cached;
    const rawOptions = context.engine.generateItems(context.targetType, context.targetCardinality as ECardinality);
    const filtered = applyDataRepeaterOptionFiltering(rawOptions, context.di, context.engine);
    const slot = composePropertySlot({
      kind: "value",
      targetType: context.targetType,
      targetCardinality: context.targetCardinality as ECardinality,
      cascaderOptions: filtered
    });
    slotCache.set(cacheKey, slot);
    return slot;
  } catch {
    // Engine failures (missing definitions, circular refs, etc.) must not crash
    // the whole manifest — just omit this property so the LLM can still see the
    // rest of the component.
    return null;
  }
};

export function buildBindablesManifest(
  sdInstance: ScreenDesignInstance,
  input: IBindablesManifestInput
): IBindablesManifestResult {
  const sd = resolveEditTarget(sdInstance, input.screenId);
  const manifest: BindablesManifest = {};
  const skipped: { kuikaId: string; error: string }[] = [];

  const platform = getPlatformContext(sd);
  const valueSlotCache = new Map<string, BindablePropertySlot>();
  let cachedEventBindings: BindablePropertySlot["options"] | null = null;
  const getEventBindings = (): BindablePropertySlot["options"] => {
    if (cachedEventBindings !== null) return cachedEventBindings;
    const defs = getAttachableActionDefinitions({
      actionDefinitions: sd.actionDefinitions ?? [],
      platform
    });
    cachedEventBindings = actionDefsToManifestBindings(defs);
    return cachedEventBindings;
  };

  const narrowing: SlotNarrowing = {
    categories: input.categories,
    maxOptionsPerCategory: input.maxOptionsPerCategory
  };
  const isNarrowed: boolean = isSlotNarrowingActive(narrowing);
  let omittedTotal: number = 0;
  // A category filter is a request for LESS output: a slot it empties is dropped
  // rather than listed with `options: []`. Without a filter every slot is kept,
  // exactly as before.
  const hasCategoryFilter: boolean = (narrowing.categories ?? []).length > 0;
  const addSlot = (
    properties: Record<string, BindablePropertySlot>,
    name: string,
    slot: BindablePropertySlot
  ): void => {
    const narrowed: BindablePropertySlot = isNarrowed ? narrowPropertySlot(slot, narrowing) : slot;
    if (hasCategoryFilter && narrowed.options.length === 0) return;
    Object.values(narrowed.omittedByCategory ?? {}).forEach((count: number): void => {
      omittedTotal += count;
    });
    properties[name] = narrowed;
  };

  for (const target of input.targets) {
    const lookup = validateKuikaId(sd, target.kuikaId);
    if ("error" in lookup) {
      skipped.push({ kuikaId: target.kuikaId, error: lookup.error });
      continue;
    }
    const di = lookup.di;

    const valueProps = target.propertyNames ?? listBindableValueProperties(sd, di);
    const eventProps = target.eventNames ?? listBindableEventNames(di);

    const properties: Record<string, BindablePropertySlot> = {};

    for (const propName of valueProps) {
      const slot = buildValueSlot(sd, target.kuikaId, propName, valueSlotCache);
      if (slot) addSlot(properties, propName, slot);
    }

    if (eventProps.length > 0) {
      const eventOptions = getEventBindings();
      for (const eventName of eventProps) {
        addSlot(properties, eventName, {
          kind: "event",
          options: eventOptions
        });
      }
    }

    const componentName = di.item?.name ?? "Component";
    const entry: BindablesManifestEntry = {
      kuikaId: target.kuikaId,
      componentName,
      properties
    };
    manifest[target.kuikaId] = entry;
  }

  const result: IBindablesManifestResult = { success: true, manifest };
  if (skipped.length > 0) result.skipped = skipped;
  if (omittedTotal > 0) {
    result.truncationNote =
      `${omittedTotal} option(s) omitted by maxOptionsPerCategory=${narrowing.maxOptionsPerCategory} ` +
      "(per-slot counts are in each slot's omittedByCategory). To see them: raise maxOptionsPerCategory, " +
      "or narrow the request with `categories`, `targets[].propertyNames` / `targets[].eventNames`.";
  }
  return result;
}
